import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useAppSearch, type SearchResult } from '@/hooks/useAppSearch';
import Nav from '@/components/Nav';

type SearchParams = {
  q?: string;
  types?: string; // comma-separated
};

export const Route = createFileRoute('/_auth/search')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const q = typeof search.q === 'string' ? search.q : '';
    const types = typeof search.types === 'string' ? search.types : undefined;
    return { q, types };
  },
});

function toHref(item: SearchResult): string | null {
  switch (item.type) {
    case 'ARTICLE':
      return `/articles/${item.slug || item.id}`;
    case 'SCAM':
      return `/scam-library/${item.slug || item.id}`;
    case 'COURSE':
      return `/courses/${item.id}`;
    case 'LESSON':
      return `/lessons/${item.id}`;
    case 'DAILY_TIP':
    case 'SCAM_OF_THE_DAY':
      return `/`; // currently surfaced on home
    case 'GLOBAL_EVENT':
      return `/footprint`; // events area
    default:
      return null;
  }
}

function TypeBadge({ type }: { type: SearchResult['type'] }) {
  const label = type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
      {label}
    </span>
  );
}

function RouteComponent() {
  const navigate = useNavigate();
  const { q = '', types } = Route.useSearch();
  const [localQ, setLocalQ] = useState(q);

  const query = useAppSearch({ q, types, limit: 20 });
  const results: SearchResult[] = Array.isArray(query.data) ? (query.data as SearchResult[]) : [];

  const selectedTypes = useMemo(() => {
    return (types || '').split(',').map(s => s.trim()).filter(Boolean);
  }, [types]);

  const toggleType = (t: string) => {
    const set = new Set(selectedTypes);
    if (set.has(t)) set.delete(t); else set.add(t);
    const next = Array.from(set).join(',');
    navigate({ to: '/search', search: { q, types: next || undefined, invitationcode: undefined, __clerk_ticket: undefined } });
  };

  const typeOptions: Array<{ key: SearchResult['type']; label: string }> = [
    { key: 'ARTICLE', label: 'Articles' },
    { key: 'SCAM', label: 'Scams' },
    { key: 'COURSE', label: 'Courses' },
    { key: 'LESSON', label: 'Lessons' },
    { key: 'DAILY_TIP', label: 'Daily Tips' },
    { key: 'SCAM_OF_THE_DAY', label: 'Scam of the Day' },
    { key: 'GLOBAL_EVENT', label: 'Events' },
  ];

  return (
    <>
      <Nav />
      <div className="min-h-screen bg-white max-w-[1100px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col gap-3 mb-4">
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Search</h1>
          <form
            className="w-full"
            onSubmit={(e) => {
              e.preventDefault();
              navigate({ to: '/search', search: { q: localQ.trim(), types, invitationcode: undefined, __clerk_ticket: undefined } });
            }}
          >
            <div className="relative w-full">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M11 4a7 7 0 105.292 12.292l3.707 3.707 1.414-1.414-3.707-3.707A7 7 0 0011 4zm0 2a5 5 0 110 10 5 5 0 010-10z" fill="currentColor"/>
              </svg>
              <input
                value={localQ}
                onChange={(e) => setLocalQ(e.target.value)}
                placeholder="Search all content…"
                className="w-full rounded-full border border-gray-300 pl-10 pr-4 py-2.5 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600 placeholder-gray-400"
              />
            </div>
          </form>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {typeOptions.map(opt => {
            const active = selectedTypes.length === 0 || selectedTypes.includes(opt.key);
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => toggleType(opt.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition ${
                  active ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                aria-pressed={active}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {q.trim().length === 0 ? (
          <div className="text-gray-600">Type a query to search across articles, lessons, courses, scams, and more.</div>
        ) : query.isLoading ? (
          <div className="text-gray-600">Searching…</div>
        ) : query.isError ? (
          <div className="text-red-600">Unable to load results.</div>
        ) : results.length === 0 ? (
          <div className="text-gray-600">No results for “{q}”.</div>
        ) : (
          <ul className="space-y-3">
            {results.map(item => {
              const href = toHref(item);
              return (
                <li key={`${item.type}:${item.id}`}>
                  {href ? (
                    <Link
                      to={href}
                      search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                      className="block rounded-xl border border-gray-200 hover:border-violet-300 hover:shadow transition bg-white p-4"
                    >
                      <div className="flex items-start gap-4">
                        {/* Left: thumbnail */}
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-12 h-12 rounded-md object-cover flex-shrink-0 border border-gray-200 bg-gray-50"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-md bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M4 4h16v16H4z" fill="currentColor" opacity="0.08" />
                              <path d="M8 7h8v2H8V7zm0 4h8v2H8v-2zm0 4h5v2H8v-2z" fill="currentColor" />
                            </svg>
                          </div>
                        )}
                        {/* Right: title + badge row and description */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="font-semibold text-gray-900">{item.title}</div>
                            <TypeBadge type={item.type} />
                          </div>
                          {item.description ? (
                            <div className="text-gray-600 text-sm line-clamp-2">{item.description}</div>
                          ) : null}
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <div className="block rounded-xl border border-gray-200 bg-gray-50 p-4 opacity-80">
                      <div className="flex items-start gap-4">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-12 h-12 rounded-md object-cover flex-shrink-0 border border-gray-200 bg-gray-50"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-md bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M4 4h16v16H4z" fill="currentColor" opacity="0.08" />
                              <path d="M8 7h8v2H8V7zm0 4h8v2H8v-2zm0 4h5v2H8v-2z" fill="currentColor" />
                            </svg>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="font-semibold text-gray-900">{item.title}</div>
                            <TypeBadge type={item.type} />
                          </div>
                          {item.description ? (
                            <div className="text-gray-600 text-sm line-clamp-2">{item.description}</div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      </div>
    </>
  );
}


